import styled from '@emotion/styled';
import { SEO, useSiteMetadata } from '@lucid/gatsby-theme-lucid';
import { ThemeProvider } from 'emotion-theming';
import React, { FC, ReactNode, useReducer, useState, useEffect } from 'react';
import { graphql } from 'gatsby';

import LayoutContext from 'contexts/layoutContext';
import { Drupal_Metatag, Drupal_Node, Maybe } from 'generated/graphql';
import theme from 'theme/theme';
import AbTestAdminUi from './ab-test-admin-ui/ab-test-admin-ui';
import { languages } from '../../data/languages';

import './layout.scss';

interface AbTest {
  [key: string]: string;
}

interface AbTestObj {
  [key: string]: AbTest;
}

interface Props {
  langPrefix: string;
  entityTranslations?: Drupal_Node[];
  metatags?: Array<Maybe<Drupal_Metatag>> | null;
  location: {
    pathname: string;
    origin: string;
  };
  pageType: string;
  children: ReactNode;
  nid?: string;
  hideSsr?: boolean;
}

const EmotionLayoutContextProvider = styled(LayoutContext.Provider)({});

const Layout: FC<Props> = ({
  langPrefix,
  location,
  pageType,
  children,
  metatags,
  entityTranslations = [],
  nid = ''
}: Props) => {
  const [showEditButton, setShowEditButton] = useState(false);
  const [showAbtestButtons, setShowAbtestButtons] = useState(false);

  const [abTests, updateTest] = useReducer(
    (tempAbTests: AbTestObj, abTest: AbTest) => {
      const theseAbTests = { ...tempAbTests };
      const { test } = abTest;
      const currentTest = tempAbTests[test] || {};

      Object.keys(abTest).forEach(key => {
        currentTest[key] = abTest[key];
      });

      theseAbTests[test] = currentTest;

      return theseAbTests;
    },
    {}
  );

  useEffect(() => {
    setShowEditButton(window.localStorage.getItem('showEditLink') === '1');
    setShowAbtestButtons(
      window.localStorage.getItem('showAbTestingUi') === '1'
    );
  }, []);

  const { cmsUrl } = useSiteMetadata();
  const { shortLangPrefix, i18nKey, urlLangPrefix } = languages[langPrefix];

  const layoutContext = {
    language: {
      langPrefix,
      shortLangPrefix,
      urlLangPrefix,
      i18nKey
    },
    location,
    pageType,
    abTesting: {
      updateTest
    }
  };

  return (
    <div
      css={{
        overflow: 'hidden'
      }}
    >
      <EmotionLayoutContextProvider value={layoutContext}>
        <SEO
          entityMetatags={metatags as Drupal_Metatag[]}
          entityTranslations={entityTranslations}
          location={location}
          pageType={pageType}
          shortLangPrefix={shortLangPrefix}
        />
        <ThemeProvider theme={theme}>
          {children}

          {showEditButton && (
            <a
              className="cms-edit-link"
              href={`${cmsUrl}/${urlLangPrefix}node/${nid}/edit`}
            >
              Edit Page
            </a>
          )}
          {showAbtestButtons && Object.keys(abTests).length > 0 && (
            <AbTestAdminUi abTests={abTests as any} />
          )}
        </ThemeProvider>
      </EmotionLayoutContextProvider>
    </div>
  );
};

export default Layout;

export const query = graphql`
  fragment marketo on Drupal_ParagraphMarketoForm {
    entityLanguage {
      id
    }
    fieldMarketoId
    fieldMarketoTitle
    fieldMarketoBody {
      processed
    }
    fieldMarketoNote {
      processed
    }
    fieldMarketoCtaText
    fieldMarketoConfirmBody
    fieldMarketoConfirmHeading
  }
`;
